/**
 * Get the merchant's opening_hours map for a location, with the
 * `_apiData → top-level` fallback that every opening-hours consumer needs.
 *
 * Context: `adaptLocation` (src/data/apiAdapter.js) preserves the raw API
 * response on `location._apiData` *and* mirrors opening_hours at the top
 * level. BookingFlow's session replacer strips `_apiData` on every
 * sessionStorage write (it's large), so after a refresh mid-flow the
 * rehydrated location has only the top-level `opening_hours`. Consumers
 * that read `_apiData?.opening_hours` without a fallback silently lose
 * access to hours after refresh — which broke calendar greying, can
 * mis-compute rental end dates, and can mis-validate return moments.
 *
 * Centralising the read makes that contract explicit and self-documenting.
 *
 * @param {Object|null|undefined} location - Adapted location (may be null
 *   on initial render, or rehydrated from sessionStorage without _apiData).
 * @returns {Object|null} opening_hours map keyed by lowercase day name,
 *   or null when the location has none / no location is set.
 */
export function getLocationOpeningHours(location) {
  return location?._apiData?.opening_hours
    || location?.opening_hours
    || null;
}

/**
 * Get the merchant's pickup_hours map for a location, with the same
 * `_apiData → top-level` fallback as getLocationOpeningHours.
 *
 * pickup_hours restricts when a rental pickup can *start* (opening_hours
 * still governs when returns must complete). Like opening_hours, it is
 * mirrored at the top level by adaptLocation and stripped from `_apiData`
 * on every sessionStorage write. Reading only `_apiData.pickup_hours`
 * therefore loses the restriction after a mid-flow refresh, silently
 * falling back to the wider opening_hours window. Centralising the read
 * keeps that contract consistent across consumers.
 *
 * @param {Object|null|undefined} location - Adapted location (may be null,
 *   or rehydrated from sessionStorage without _apiData).
 * @returns {Object|null} pickup_hours map keyed by lowercase day name,
 *   or null when the location has none / no location is set.
 */
export function getLocationPickupHours(location) {
  return location?._apiData?.pickup_hours
    || location?.pickup_hours
    || null;
}

/**
 * Whether this location restricts pickup/delivery ("handoff") times to those
 * whose rental return falls within opening hours.
 *
 * When true: multi_day handoffs must occur strictly before that day's opening
 * close (no next-day-return roll), and delivery returns must fall within opening
 * hours (bringing delivery up to the validation pickups already do). When false
 * (default): legacy behavior. See isHandoffSlotAllowed for the full matrix.
 * Same `_apiData → top-level` fallback as the hours readers above, so the flag
 * survives a mid-flow sessionStorage refresh.
 *
 * @param {Object|null|undefined} location - Adapted location.
 * @returns {boolean} true if the restriction is enabled (default false).
 */
export function getLocationRestrictHandoffs(location) {
  return location?._apiData?.restrict_handoffs_to_opening_hours
    ?? location?.restrict_handoffs_to_opening_hours
    ?? false;
}
